import * as React from 'react'

import { BadgeStyled } from './styles/BadgeStyled'

export interface IBadgeProps {
  /**
   * Accessible label for screen readers.
   */
  ariaLabel?: string
  /**
   * Sets the badge color.
   */
  color?: 'primary' | 'danger'
  /**
   * Maximum value to display before showing as `max+`.
   */
  maxValue?: number
  /**
   * Content to display inside the badge (usually a number or short string).
   * */
  children: React.ReactNode
}

export const DEFAULT_MAX_VALUE = 99

function isValidMaxValue(value: any): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}

const isNumeric = (value: unknown): value is number | string =>
  (typeof value === 'number' && Number.isFinite(value)) ||
  (typeof value === 'string' &&
    value.trim() !== '' &&
    Number.isFinite(Number(value)))

/**
 * Badge component displays a small status indicator, typically used to show counts or status
 * on icons, buttons, or other UI elements.
 */
export const Badge = ({
  ariaLabel,
  color = 'primary',
  maxValue = DEFAULT_MAX_VALUE,
  children,
}: IBadgeProps) => {
  const validMaxValue = isValidMaxValue(maxValue) ? maxValue : DEFAULT_MAX_VALUE

  let displayValue = children
  if (isNumeric(children)) {
    const childValue = Number(children)
    displayValue = childValue > validMaxValue ? `${validMaxValue}+` : childValue
  }

  return (
    <BadgeStyled color={color} aria-label={ariaLabel}>
      {displayValue}
    </BadgeStyled>
  )
}
